import { AsyncPipe, NgClass } from '@angular/common';
import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { RouterLink } from '@angular/router';
import {
  DynamicFormModel,
  DynamicFormService,
  DynamicInputModel,
  DynamicRadioGroupModel,
  DynamicSelectModel,
} from '@ng-dynamic-forms/core';
import {
  CmsService,
  GlobalMessageService,
  GlobalMessageType,
  LanguageService,
  RoutingService,
  TranslatePipe,
  TranslationService,
} from '@spartacus/core';
import { BREAKPOINT, BreakpointService } from '@spartacus/storefront';
import { Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { BaseSiteConfigService } from 'src/app/service/base-site/base-site-config.service';
import { ComponentService } from 'src/app/service/component.service';
import {
  ManagementEmployeeService,
  PostAddEmployeesPayload,
} from 'src/app/service/myaccount/management-employee.service';
import { MyaccountCredentialsService } from 'src/app/service/myaccount/myaccount-credentials.service';
import {
  GetB2BUnitAddPayload,
  ShimanoDynamicFormService,
} from 'src/app/service/shimano-dynamic-form/shimano-dynamic-form.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { DynamicFormsComponent } from '../../dynamic-forms/dynamic-forms.component';
import { MyaccountEmployeeAddressComponent } from '../myaccount-employee-address/myaccount-employee-address.component';
import { MyaccountPermissionsComponent } from '../myaccount-permissions/myaccount-permissions.component';

@Component({
  selector: 'app-edit-employee',
  templateUrl: './edit-employee.component.html',
  styleUrls: ['./edit-employee.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [AsyncPipe],
  imports: [
    NgClass,
    DynamicFormsComponent,
    MyaccountEmployeeAddressComponent,
    MyaccountPermissionsComponent,
    RouterLink,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class EditEmployeeComponent implements OnInit, OnDestroy, AfterContentChecked {
  formGroup: FormGroup;
  editEmployeeFormModel: DynamicFormModel;
  userAddresses$: Observable<any>;
  currentPageSubscription$: Subscription;
  editEmployeeModelSubscription$: Subscription;
  editEmployeeFormGroupSubscription$: Subscription;
  b2bUnitAddressSubscription$: Subscription;
  errorMessage: any;
  count = 0;
  isAdmin = false;
  isMobile = false;
  isSelected: any;
  cxMobileWidth = this.breakpointService.getSize(BREAKPOINT.sm);
  currentAddressID: string;
  inputAddressesModel: DynamicSelectModel<string>;
  firstNameInput: DynamicInputModel;
  lastNameInput: DynamicInputModel;
  emailInput: DynamicInputModel;
  jobTitleInput: DynamicInputModel;
  selectedAddress: DynamicSelectModel<string>;
  radioStatus: DynamicRadioGroupModel<boolean>;
  myAccountPermissionSubscription$: Subscription;
  accountPermissionsEnabled: boolean;
  b2bUnitAddressesPayload: GetB2BUnitAddPayload = {
    fields: 'DEFAULT',
  };
  hasFormChanges: boolean = false;
  buttonNoUpdate = true;
  accountPermissions: any = [];
  currentPermissions: any = [];
  accountAddresses: any = [];
  defaultAddress: any;
  hasPermissionShipToAddressChanges = false;
  formCheckValueChangesSubs: Subscription;
  shipToAddressSubscription: Subscription;
  showSnahAccountPermissionSubs: Subscription;
  isAddressSelected: boolean = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private componentService: ComponentService,
    private cmsService: CmsService,
    private formService: DynamicFormService,
    private breakpointService: BreakpointService,
    private globalMessageService: GlobalMessageService,
    private dynamicFormService: ShimanoDynamicFormService,
    private manageEmployeeService: ManagementEmployeeService,
    private routingService: RoutingService,
    private messageService: GlobalMessageService,
    private mycredentials: MyaccountCredentialsService,
    protected translation: TranslationService,
    private languageService: LanguageService,
    private pipeAsync: AsyncPipe,
    private baseSiteConfigService: BaseSiteConfigService,
    private storageService: StorageService
  ) {}

  ngOnInit() {
    this.mycredentials
      .getYourCredentials()
      .pipe(take(1))
      .subscribe((results: any) => {
        if (typeof results !== 'undefined') {
          for (const key in results) {
            if (key === 'uid') {
              if (results[key].localeCompare(this.storageService.getSessionStorageItem('useruid')) === 0) {
                this.isAdmin = true;
                this.componentService.setUserAdmin(this.isAdmin);
              }
            }
          }
        }
      });

    this.currentAddressID = this.storageService.getSessionStorageItem('address');
    this.currentPageSubscription$ = this.cmsService.getCurrentPage().subscribe((currentPage) => {
      if (currentPage && currentPage.pageId.includes('accountEditEmployeePage')) {
        this.editEmployeeModelSubscription$ = this.componentService.dynamicFormModel$.subscribe(
          (editEmployeeFormModel) => {
            if (editEmployeeFormModel) {
              this.count = 0;
              this.updateFormModel(editEmployeeFormModel);
              this.setEditEmployeeInitValues();
            }
          }
        );

        this.editEmployeeFormGroupSubscription$ = this.componentService.dynamicFormGroup$.subscribe(
          (editEmployeeFormGroup) => {
            if (editEmployeeFormGroup) {
              this.updateFormGroup(editEmployeeFormGroup);
              if (this.formGroup.get('userStatus')) {
                this.formGroup.get('userStatus').setValue(this.storageService.getSessionStorageItem('status'));
              }
              this.cdr.detectChanges();
            }
          }
        );
      }
    });

    this.isMyAccountPermissionsEnabled();
  }

  ngAfterContentChecked() {
    this.cdr.detectChanges();
  }

  getTranslatedError(id: string): Observable<string> {
    return this.translation.translate(`error.dynamicform.${id}`);
  }

  updateFormGroup(formGroup: any): void {
    this.formGroup = formGroup;
    this.checkFormGroupChanged(this.formGroup);
    this.disableButton();

    /* FOR SEU AND OTHER SSO ONLY */
    if (!this.accountPermissionsEnabled) {
      if (this.formGroup.value) {
        this.isAddressSelected = true;
      }
    }
  }

  updateFormModel(formModel: any) {
    this.editEmployeeFormModel = formModel;

    this.userAddresses$ = this.dynamicFormService.getB2BUnitAddresses(this.b2bUnitAddressesPayload);
    this.userAddresses$.pipe(take(1)).subscribe((data) => {
      if (typeof data.items !== 'undefined') {
        if (data.items.length > 0 && this.count === 0) {
          const optionCountriesValues: any = [];
          this.count = data.items.length;
          for (const key in data.items) {
            if (key !== '' || key !== null) {
              if (data.items[key].name.localeCompare(this.storageService.getSessionStorageItem('formattedadd')) === 0) {
                this.isSelected = data.items[key].uid;
              }
              const options = {
                label: data.items[key].name,
                value: data.items[key].uid,
              };
              optionCountriesValues.push(options);
            }
          }
          this.formGroup.get('deliveryAddress')?.setValue(this.isSelected);
          this.inputAddressesModel = this.formService.findModelById<DynamicSelectModel<string>>(
            'deliveryAddress',
            formModel
          );
          if (this.inputAddressesModel !== null) {
            this.inputAddressesModel.options = optionCountriesValues;
            this.formService.detectChanges();
          }
        }
      }
    });

    this.radioStatus = this.formService.findModelById<DynamicRadioGroupModel<boolean>>(
      'userStatus',
      this.editEmployeeFormModel
    );
    if (this.radioStatus) {
      const hasError = {
        required: this.pipeAsync.transform(this.getTranslatedError('userStatus')),
      };
      this.radioStatus.errorMessages = hasError;
    }

    this.lastNameInput = this.formService.findModelById<DynamicInputModel>(
      'lastName_medium',
      this.editEmployeeFormModel
    );
    if (this.lastNameInput) {
      const hasError = {
        required: this.pipeAsync.transform(this.getTranslatedError('lastName_medium')),
      };
      this.lastNameInput.errorMessages = hasError;
    }
    this.firstNameInput = this.formService.findModelById<DynamicInputModel>(
      'firstName_medium',
      this.editEmployeeFormModel
    );
    if (this.firstNameInput) {
      const hasError = {
        required: this.pipeAsync.transform(this.getTranslatedError('firstName_medium')),
      };
      this.firstNameInput.errorMessages = hasError;
    }
    this.emailInput = this.formService.findModelById<DynamicInputModel>(
      'emailAddress_medium',
      this.editEmployeeFormModel
    );
    if (this.emailInput) {
      const hasError = {
        required: this.pipeAsync.transform(this.getTranslatedError('emailAddress_full')),
        pattern: this.pipeAsync.transform(this.getTranslatedError('emailAddress_medium')),
      };
      this.emailInput.errorMessages = hasError;
    }
    this.jobTitleInput = this.formService.findModelById<DynamicInputModel>('jobTitle', this.editEmployeeFormModel);
    if (this.jobTitleInput) {
      const hasError = {
        required: this.pipeAsync.transform(this.getTranslatedError('jobTitle')),
      };
      this.jobTitleInput.errorMessages = hasError;
    }
    this.selectedAddress = this.formService.findModelById<DynamicSelectModel<string>>(
      'deliveryAddress',
      this.editEmployeeFormModel
    );
    if (this.selectedAddress) {
      const hasError = {
        required: this.pipeAsync.transform(this.getTranslatedError('deliveryAddress')),
      };
      this.selectedAddress.errorMessages = hasError;
    }

    this.formService.detectChanges();
  }

  checkFormGroupChanged(employeeGroup: FormGroup) {
    this.formCheckValueChangesSubs = employeeGroup.valueChanges.subscribe((data) => {
      if (this.checkFormValues(data)) {
        this.hasFormChanges = true;
      } else {
        this.hasFormChanges = false;
      }
    });
  }

  checkFormValues(data: any): boolean {
    return (
      data.userStatus !== this.storageService.getSessionStorageItem('status') ||
      data.firstName_medium !== this.storageService.getSessionStorageItem('firstname') ||
      data.jobTitle !== this.storageService.getSessionStorageItem('jobtitle') ||
      data.emailAddress_medium !== this.storageService.getSessionStorageItem('email') ||
      data.lastName_medium !== this.storageService.getSessionStorageItem('lastname') ||
      (data.deliveryAddress && data.deliveryAddress !== this.isSelected)
    );
  }

  setEditEmployeeInitValues() {
    if (this.radioStatus && this.lastNameInput && this.firstNameInput && this.emailInput && this.jobTitleInput) {
      if (this.storageService.getSessionStorageItem('status')) {
        this.radioStatus.value = true;
      } else {
        this.radioStatus.value = false;
      }
      if (this.storageService.getSessionStorageItem('lastname') !== 'undefined') {
        this.lastNameInput.value = this.storageService.getSessionStorageItem('lastname');
      } else {
        this.lastNameInput.value = null;
      }
      if (this.storageService.getSessionStorageItem('firstname') !== 'undefined') {
        this.firstNameInput.value = this.storageService.getSessionStorageItem('firstname');
      } else {
        this.firstNameInput.value = null;
      }
      if (this.storageService.getSessionStorageItem('email') !== 'undefined') {
        this.emailInput.value = this.storageService.getSessionStorageItem('email');
      } else {
        this.emailInput.value = null;
      }
      if (this.storageService.getSessionStorageItem('jobtitle') !== 'undefined') {
        this.jobTitleInput.value = this.storageService.getSessionStorageItem('jobtitle');
      } else {
        this.jobTitleInput.value = null;
      }
    }
  }

  onSubmit($event: any, request: string) {
    if (typeof this.formGroup !== 'undefined') {
      if (this.formGroup.status.toLowerCase() === 'valid') {
        if (this.accountPermissionsEnabled) {
          this.componentService.accountPermissions$.subscribe((userPermissions) => {
            this.accountPermissions =
              this.currentPermissions !== undefined && this.currentPermissions.length > 0
                ? this.currentPermissions
                : userPermissions;
          });

          this.accountAddresses =
            this.accountAddresses !== undefined && this.accountAddresses.length > 0
              ? this.accountAddresses
              : JSON.parse(this.storageService.getSessionStorageItem('shipToAddresses'));
        }

        this.defaultAddress =
          this.formGroup.value.deliveryAddress !== undefined
            ? this.formGroup.value.deliveryAddress
            : this.accountAddresses !== undefined
            ? this.accountAddresses[0]
            : '';

        const editEmployeePayload: PostAddEmployeesPayload = {
          active: this.formGroup.value.userStatus === 'true' ? true : false,
          firstName: this.formGroup.value.firstName_medium,
          lastName: this.formGroup.value.lastName_medium,
          jobTitle: this.formGroup.value.jobTitle,
          id: this.defaultAddress,
          email: this.formGroup.value.emailAddress_medium,
          customerId: this.storageService.getSessionStorageItem('useruid'),
          shipToAddrsId: this.accountAddresses,
          storeFrontPermissions: this.accountPermissions,
        };
        this.manageEmployeeService
          .patchEditEmployee(editEmployeePayload)
          .pipe(take(1))
          .subscribe(
            (data) => {
              if (data === null) {
                this.storageService.removeSessionStorageItem('status');
                this.storageService.removeSessionStorageItem('lastname');
                this.storageService.removeSessionStorageItem('firstname');
                this.storageService.removeSessionStorageItem('email');
                this.storageService.removeSessionStorageItem('jobtitle');
                this.storageService.removeSessionStorageItem('address');
                this.storageService.removeSessionStorageItem('formattedadd');
                this.storageService.removeSessionStorageItem('useruid');
                this.storageService.removeSessionStorageItem('storeFrontPermissions');
                this.storageService.removeSessionStorageItem('shipToAddresses');
                this.routingService.goByUrl('/my-account');
                this.messageService.add(
                  {
                    key: 'myAccount.successEditEmployee',
                    params: {
                      firstName: editEmployeePayload.firstName,
                      lastName: editEmployeePayload.lastName,
                    },
                  },
                  GlobalMessageType.MSG_TYPE_CONFIRMATION
                );
              }
            },
            (err) => {
              this.globalMessageService.add(err.error.errors[0].message, GlobalMessageType.MSG_TYPE_ERROR);
            }
          );
      }
    }
  }

  get activeLanguage$(): Observable<string> {
    return this.languageService.getActive();
  }

  isMyAccountPermissionsEnabled = () => {
    this.myAccountPermissionSubscription$ = this.getMyAccountPermissionSiteConfig$().subscribe((permisssion) => {
      this.accountPermissionsEnabled = permisssion;
      this.cdr.detectChanges();
    });
  };

  getMyAccountPermissionSiteConfig$(): Observable<boolean> {
    const key = 'shimano.config.storefront.permission.enabled';
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$([key])
      .pipe(
        map((configs) => configs && configs.filter((config: any) => config.key === key).map((res: any) => res.value)[0])
      );
  }

  hasAddressSelected(value: boolean) {
    if (!this.accountPermissionsEnabled) this.isAddressSelected = value;
  }

  activePermission(permissions: any) {
    this.currentPermissions = permissions;
  }

  activeAddresses(addresses: any) {
    this.accountAddresses = addresses;
    if (this.accountPermissionsEnabled) this.isAddressSelected = this.accountAddresses.length > 0;
  }

  hasChanged(hasChanged: boolean) {
    this.hasPermissionShipToAddressChanges = hasChanged;
    this.disableButton();
    this.cdr.detectChanges();
  }

  disableButton() {
    if (!this.hasFormChanges && !this.hasPermissionShipToAddressChanges) {
      this.buttonNoUpdate = true;
    } else if (this.hasFormChanges) {
      this.buttonNoUpdate = false;
    } else if (this.hasPermissionShipToAddressChanges) {
      this.buttonNoUpdate = false;
    } else {
      this.buttonNoUpdate = true;
    }
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.currentPageSubscription$?.unsubscribe();
    this.editEmployeeModelSubscription$?.unsubscribe();
    this.editEmployeeFormGroupSubscription$?.unsubscribe();
    this.b2bUnitAddressSubscription$?.unsubscribe();
    this.myAccountPermissionSubscription$?.unsubscribe();
    this.formCheckValueChangesSubs?.unsubscribe();
    this.showSnahAccountPermissionSubs?.unsubscribe();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < this.cxMobileWidth;
  }
}
